import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { autoUpdate, flip, FloatingPortal, shift, size, useFloating } from "@floating-ui/react";
import { ListOption, Stack } from "@skailer-com/uikit";
import { CommandProps, Editor, Range } from "@tiptap/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export interface MentionListProps extends CommandProps {
  items: ApiEmployee[];
  insert: (item: ApiEmployee, text: string) => void;
  position: { x: number; y: number } | null;
  editor: Editor;
  range: Range;
}

export const EmployeeMentionList = ({
  items,
  insert,
  position,
  editor,
  range,
}: MentionListProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { renderEmployeeName } = useFormatter();
  const { t } = useTranslation();

  const {
    refs: { setReference, setFloating },
    floatingStyles,
    update,
  } = useFloating({
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      flip(),
      shift(),
      size({
        apply({ availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`,
          });
        },
        padding: 10,
      }),
    ],
  });

  useEffect(() => {
    if (!position || !editor) return;
    const domNode = editor.view.domAtPos(range.from)?.node as HTMLElement;
    if (domNode) {
      setReference(domNode);
      update();
    }
  }, [position, editor, range, setReference, update]);

  const insertEmit = (index: number) => {
    const employee = items[index];

    if (!employee) return;

    insert(employee, renderEmployeeName(employee));
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) {
        event.stopPropagation();
        event.preventDefault();
      }

      switch (event.key) {
        case "ArrowDown":
          setSelectedIndex((prev) => (prev + 1) % items.length);
          break;
        case "ArrowUp":
          setSelectedIndex((prev) => (prev - 1) % items.length);
          break;
        case "Enter":
          insertEmit(selectedIndex);
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [items, selectedIndex, insert]);

  if (!position) return null;

  return (
    <FloatingPortal>
      <Stack
        ref={setFloating}
        className={twMerge("bg-light shadow-md p-2 rounded-sm z-popover overflow-y-auto")}
        style={floatingStyles}
        gap={"xs"}
      >
        {items.length == 0 && <>{t("core:empty_mention_text")}</>}
        {items.map((item, index) => (
          <Stack
            key={`mention-${item.id}`}
            onClick={() => {
              insertEmit(index);
            }}
            className={twMerge("rounded-xs", `${index === selectedIndex ? "bg-dark/5" : ""}`)}
            direction={"horizontal"}
            gap={"sm"}
          >
            <ListOption
              className={"w-full"}
              leftIcon={<EmployeeAvatar employee={item} size={"xs"} />}
              title={renderEmployeeName(item)}
              caption={getEmployeePositionTitle(item)}
            ></ListOption>
          </Stack>
        ))}
      </Stack>
    </FloatingPortal>
  );
};
